import type { CSSProperties, MouseEvent } from 'react';

import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import { Sticky } from 'react-sticky';

import Button from 'src/app/components/Elements/Button';
import { HeaderLogoLink } from 'src/app/components/Elements/HeaderLogoLink';
import { HorizontalProgressBar } from 'src/app/components/Elements/ProgressBars/HorizontalProgressBar/index';
import * as paths from 'src/app/router/paths';
import { getUrlPathWithPreservedQuery } from 'src/app/utils';

import { NavTheme, ResponsiveView, Variant } from '../../constants';
import { BottomNav, DesktopHeaderContainer, DesktopLeftNav, DesktopRightNav, NavWrapper } from '../../styles';
import styles from '../../styles.module.scss';

interface DesktopHeaderProps {
  currentStepIndex: number;
  getRedirectClickHandler: (handler: () => void) => (e: MouseEvent) => void;
  handleGetStartedClick: () => void;
  isHowToPage: boolean;
  isOnboardingFlow: boolean;
  navOpacity: number;
  navTheme: NavTheme;
  progressBarFillPercent: number;
  progressBarTotalSteps: number;
  showMenu: boolean;
  hasGetStartedLink?: boolean;
  hasSignInLink: boolean;
  stickyTopOffset: number;
  variant: Variant;
}

export const DesktopHeader = ({
  currentStepIndex,
  getRedirectClickHandler,
  handleGetStartedClick,
  isHowToPage,
  isOnboardingFlow,
  navOpacity,
  navTheme,
  progressBarFillPercent,
  progressBarTotalSteps,
  hasGetStartedLink = true,
  hasSignInLink,
  showMenu,
  stickyTopOffset,
  variant,
}: DesktopHeaderProps) => {
  return (
    <Sticky topOffset={stickyTopOffset}>
      {({ style }: { style: CSSProperties }) => (
        <NavWrapper
          style={{
            position: 'relative',
            opacity: navOpacity,
            ...style,
          }}
          responsiveView={ResponsiveView.desktop}
          navTheme={navTheme}
          hasFlexWrap={isOnboardingFlow}
          isOnboardingFlow={isOnboardingFlow}
        >
          <DesktopHeaderContainer isOnboardingFlow={isOnboardingFlow}>
            <DesktopLeftNav>
              <HeaderLogoLink />
              {variant !== Variant.simple && (
                <>
                  <NavLink
                    className={classNames(styles.headerLink, styles.callout)}
                    to={getUrlPathWithPreservedQuery(paths.howItWorks.toPath())}
                    activeClassName={styles.activeLink}
                  >
                    How it works
                  </NavLink>

                  <NavLink
                    className={classNames(styles.headerLink, styles.callout)}
                    to={getUrlPathWithPreservedQuery(paths.about.toPath())}
                    activeClassName={styles.activeLink}
                  >
                    About us
                  </NavLink>

                  <NavLink
                    className={classNames(styles.headerLink, styles.callout)}
                    to={getUrlPathWithPreservedQuery(paths.doctors.toPath())}
                    activeClassName={styles.activeLink}
                  >
                    For doctors
                  </NavLink>

                  <NavLink
                    className={classNames(styles.headerLink, styles.callout)}
                    to={getUrlPathWithPreservedQuery(paths.questions.toPath())}
                    onClick={getRedirectClickHandler(paths.redirectQuestions)}
                    activeClassName={styles.activeLink}
                  >
                    Questions
                  </NavLink>
                </>
              )}
            </DesktopLeftNav>
            <DesktopRightNav>
              {hasSignInLink && (
                <NavLink
                  className={classNames(styles.headerLink, styles.callout)}
                  to={getUrlPathWithPreservedQuery(paths.login.toPath())}
                  activeClassName={styles.activeLink}
                >
                  Sign in
                </NavLink>
              )}
              {variant !== Variant.simple && hasGetStartedLink && !isHowToPage && (
                <Button
                  size="medium"
                  style={navTheme === NavTheme.blue ? 'newSecondary' : 'primary'}
                  onClick={handleGetStartedClick}
                  fixedWidth
                >
                  Get started
                </Button>
              )}
            </DesktopRightNav>
          </DesktopHeaderContainer>

          {isOnboardingFlow && (
            <BottomNav showMenu={showMenu}>
              <HorizontalProgressBar
                currentStep={currentStepIndex + 1}
                totalSteps={progressBarTotalSteps}
                fillPercent={progressBarFillPercent}
                size={'large'}
                isHiddenOnDesktop={false}
                hasLargeBorderRadius
              />
            </BottomNav>
          )}
        </NavWrapper>
      )}
    </Sticky>
  );
};
